/* global process */
import { Sort } from '@/hooks/useSeeds';
import {
  getAllowedFilters,
  getDefaultSort,
  getSeedTypeConfig,
  SeedTypeConfig,
} from '@/sections/seeds/config/seedTypeConfigs';
import { FILTER_DROPDOWN_WIDTHS } from '@/sections/seeds/constants';
import { SeedType } from '@/sections/seeds/types';

/**
 * Seeds Filter Configuration
 *
 * Constants extracted to constants/ directory (Phase 1 - 2025-10-22)
 * Location: src/sections/seeds/constants/filterWidths.ts
 *
 * Filter field visibility and filter-specific helper functions.
 * NO JSX - pure TypeScript only.
 *
 * Pattern matches:
 * - src/sections/seeds/config/columns.ts (column widths)
 */

// Re-export constant for backward compatibility
export { FILTER_DROPDOWN_WIDTHS };

/**
 * Get dropdown width for a filter field
 *
 * @param fieldName - The filter field name
 * @returns The configured width or default width
 *
 * @example
 * ```tsx
 * const width = getFilterDropdownWidth('registrar'); // 1000
 * const width = getFilterDropdownWidth('status'); // 400 (default)
 * ```
 */
export const getFilterDropdownWidth = (fieldName: string): number => {
  return (
    FILTER_DROPDOWN_WIDTHS[fieldName as keyof typeof FILTER_DROPDOWN_WIDTHS] ||
    FILTER_DROPDOWN_WIDTHS.default
  );
};

// ============================================================================
// FILTER FIELD VISIBILITY CONFIGURATION
// ============================================================================

/**
 * Type-specific filter field configurations
 *
 * Defines which filter fields are available for each seed type:
 * - Domain: status, source, tags, email, registrar, registrant
 * - IP/CIDR: status, source, tags, country, registrar, registrant
 * - WebApplication: status, source, tags
 */
export interface FilterFieldConfig {
  /** Filter field name */
  field: string;
  /** Whether field should be shown for this seed type */
  visible: boolean;
}

/**
 * Check if a filter field should be shown for a seed type
 *
 * **Config-driven implementation** - Uses declarative configuration
 * instead of 50+ lines of conditionals.
 *
 * @param fieldName - The filter field name
 * @param seedType - The seed type (domain, ip, webapplication, preseed)
 * @returns Whether the field should be shown
 *
 * @example
 * ```tsx
 * const showCountry = shouldShowFilterField('country', 'ip'); // true
 * const showCountryOnDomain = shouldShowFilterField('country', 'domain'); // false
 * const showStatusOnPreseed = shouldShowFilterField('status', 'preseed'); // true
 * const showEmailOnPreseed = shouldShowFilterField('email', 'preseed'); // false
 * ```
 */
export const shouldShowFilterField = (
  fieldName: string,
  seedType: SeedType
): boolean => {
  const allowedFilters = getAllowedFilters(seedType);
  return allowedFilters.includes(fieldName);
};

// ============================================================================
// FILTER CLEANUP UTILITIES
// ============================================================================

/**
 * Clean up filters to remove fields that shouldn't be visible on the current tab
 *
 * **Config-driven implementation** - Uses declarative configuration
 * instead of 80+ lines with preseed special cases.
 *
 * This function prevents cross-tab filter contamination by:
 * 1. Removing filter fields not valid for the target tab (using config.allowedFilters)
 * 2. Applying correct default sort for the target tab type
 * 3. Handling field mappings for preseed compatibility
 *
 * @param filters - The current filter state
 * @param seedType - The target seed type
 * @returns Cleaned filter state with invalid filters removed
 *
 * @example
 * ```tsx
 * // Remove country filter when switching to domain tab
 * const cleaned = cleanupFiltersForTab(
 *   { type: 'ip', advancedFilters: { country: ['US'], status: ['A'] } },
 *   'domain'
 * );
 * // Result: { type: 'domain', advancedFilters: { status: ['A'] }, sort: {...} }
 *
 * // Keep only status filter for preseed
 * const cleaned = cleanupFiltersForTab(
 *   { type: 'domain', advancedFilters: { email: ['test'], status: ['A'] } },
 *   'preseed'
 * );
 * // Result: { type: 'domain', advancedFilters: { status: ['A'] }, sort: { orderBy: 'group', ... } }
 * ```
 */
export const cleanupFiltersForTab = (
  filters: {
    type?: SeedType;
    advancedFilters?: Record<string, string[]>;
    sort?: Sort;
    search?: string;
  },
  seedType: SeedType
): {
  type?: SeedType;
  advancedFilters?: Record<string, string[]>;
  sort?: Sort;
  search?: string;
} => {
  const config = getSeedTypeConfig(seedType);
  const allowedFilters = config.allowedFilters;

  // Clean advancedFilters based on allowed filters for this type
  const cleanedAdvancedFilters: Record<string, string[]> = {};

  if (filters.advancedFilters) {
    Object.entries(filters.advancedFilters).forEach(([key, value]) => {
      if (allowedFilters.includes(key)) {
        cleanedAdvancedFilters[key] = value;
      } else if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[Seeds] Removing invalid filter "${key}" from ${seedType} tab`
        );
      }
    });
  }

  // Get appropriate default sort for target type
  const defaultSort = getDefaultSort(seedType) as Sort;

  // Apply field mappings if needed (for preseed compatibility)
  // If switching types, use the default sort for the target type
  const sort =
    filters.sort && filters.type === seedType && config.fieldMappings
      ? mapSortField(filters.sort, config.fieldMappings)
      : filters.sort && filters.type === seedType
        ? filters.sort
        : defaultSort;

  return {
    type: seedType,
    search: filters.search,
    sort,
    advancedFilters:
      Object.keys(cleanedAdvancedFilters).length > 0
        ? cleanedAdvancedFilters
        : undefined,
  };
};

/**
 * Map sort field using field mappings (for preseed compatibility)
 *
 * Preseed has different field names (type, value, title) that need to be
 * mapped to standard Seed fields (group, identifier, name) for sorting.
 *
 * @param sort - The current sort configuration
 * @param mappings - Field mappings from config
 * @returns Sort configuration with mapped field names
 *
 * @example
 * ```typescript
 * const mappings = { group: 'type', identifier: 'value' };
 * const sort = { orderBy: 'identifier', descending: true };
 * const mapped = mapSortField(sort, mappings);
 * // Result: { orderBy: 'value', descending: true }
 * ```
 */
const mapSortField = (
  sort: Sort,
  mappings: SeedTypeConfig['fieldMappings']
): Sort => {
  // If sorting by a mapped field, translate it
  const orderByKey = sort.orderBy as string;
  const mapping = mappings?.[orderByKey as keyof typeof mappings];

  return {
    orderBy: mapping || orderByKey,
    descending: sort.descending,
  } as Sort;
};
